import React, {FC, useEffect, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {VenmoWordmark as VenmoIcon, Icon} from '@optimaxdev/icons';
import type {BraintreeError} from 'braintree-web/core';
import type {Venmo} from 'braintree-web/venmo';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';
import {PayerInfoType} from 'desktop/types/payerInfoType';
import {PaymentType} from 'types/PaymentMethodsType';

import {PAYMENTS} from '../../constants/paymentStep';
import {setOptionPaymentMethod as setOptionPaymentMethodAction} from '../../store/reducers/analytics/analytics';
import {sendPayerInfoToSaga} from '../../store/reducers/paymentStep/payerInfo/payerInfo';
import {
    getIsSelectedByKey,
    getPaymentMethodByKey,
} from '../../store/selectors/paymentStep/paymentStep';
import {PaymentVenmoButtonConnected} from '../paymentPrimerVenmo/paymentVenmoButton/paymentVenmoButton';
import {PaymentWrapperConnected} from '../paymentWrapper/paymentWrapper';

import {VenmoTokenizePayloadType} from './venmoTokenizePayloadType';

import s from './paymentVenmoWrapper.scss';

type PropsType = {
    method: PaymentType;
    isSelected: boolean;
    runSavePayment: (data: PayerInfoType) => void;
    setOptionPaymentMethod: () => void;
};

/**
 * Payment VENMO for checkout
 */
// eslint-disable-next-line max-statements
const PaymentVenmoWrapper: FC<PropsType> = ({
    isSelected,
    method,
    runSavePayment,
    setOptionPaymentMethod,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const [stateVenmoInstance, setStateVenmoInstance] = useState<Venmo | null>(null);
    const [isPaymentAuthorized, setIsPaymentAuthorized] = useState<boolean>(false);
    const [payerInfo, setPayerInfo] = useState<PayerInfoType | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleVenmoError = (e: BraintreeError) => {
        setErrorMessage(e.message);

        sendDesktopAnalyticsEvent(
            CHECKOUT_PAYMENT_STEP,
            {
                message: `Error – ${e.message}`,
                method: 'Venmo',
            },
            false,
        );
    };

    const createVenmo = () => {
        // Creating macro task to delay this in event loop future cycle
        setTimeout(async () => {
            const [client, venmo] = await Promise.all([
                import(/* webpackChunkName: "braintree-web" */ 'braintree-web/client'),
                import(/* webpackChunkName: "braintree-web" */ 'braintree-web/venmo'),
            ]);
            client
                .create({
                    authorization: typeof method.button === 'object' ? method.button?.token : '',
                })
                .then(clientInstance => {
                    // Create a Venmo component.
                    return venmo
                        .create({
                            allowDesktop: true,
                            allowNewBrowserTab: false,
                            client: clientInstance,
                            mobileWebFallBack: true,
                            paymentMethodUsage: 'single_use',
                        })
                        .then(venmoInstance => {
                            // Verify browser support before proceeding.
                            if (!venmoInstance.isBrowserSupported()) {
                                return;
                            }
                            setStateVenmoInstance(venmoInstance);
                        })
                        .catch((venmoErr: BraintreeError) => {
                            handleVenmoError(venmoErr);
                        });
                })
                .catch(err => {
                    // Handle component creation error
                    handleVenmoError(err);
                    sendDesktopAnalyticsEvent(
                        CHECKOUT_PAYMENT_STEP,
                        {
                            message: `Error – Loading Error - ${err.message}`,
                            method: 'Venmo',
                        },
                        false,
                    );
                });
        });
    };

    useEffect(() => {
        createVenmo();
        // createVenmo should be called once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onVenmoClick = async () => {
        try {
            if (stateVenmoInstance) {
                const {
                    details: {payerInfo: venmoInfo},
                    nonce,
                } = (await stateVenmoInstance.tokenize()) as unknown as VenmoTokenizePayloadType;
                setIsPaymentAuthorized(true);

                setPayerInfo({nonce, ...venmoInfo, method: PAYMENTS.METHOD_VENMO});
                setOptionPaymentMethod();
            }
        } catch (e) {
            if (e.code === 'VENMO_DESKTOP_CANCELED') return;
            handleVenmoError(e);
        }
    };

    if (!stateVenmoInstance) return null;

    return (
        <PaymentWrapperConnected
            title={
                <div className={classNames(s.title, {[s.titleFacelift]: isFacelift})}>
                    <Icon icon={VenmoIcon} />
                </div>
            }
            code={method.code}
            isSelected={isSelected}
        >
            <PaymentVenmoButtonConnected
                onSubmit={() => {
                    if (payerInfo) runSavePayment(payerInfo);
                }}
                isPaymentAuthorized={isPaymentAuthorized}
                onClick={onVenmoClick}
                venmoErrorMessage={errorMessage}
            />
        </PaymentWrapperConnected>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    isSelected: getIsSelectedByKey(store, PAYMENTS.METHOD_VENMO),
    method: getPaymentMethodByKey(store, PAYMENTS.METHOD_VENMO),
});

const mapDispatchToProps = {
    runSavePayment: sendPayerInfoToSaga,
    setOptionPaymentMethod: setOptionPaymentMethodAction,
};

export const PaymentVenmoWrapperConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(PaymentVenmoWrapper);
